import React, { useEffect, useState } from 'react';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { RadioButton } from 'primereact/radiobutton';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import dayjs from 'dayjs';
import { lagerFinishedProductsAPI, lagerProductionAPI, lagerTransportAPI } from '../services/api';
import { LagerFinishedProduct, LagerProductionRecord, LagerTransport } from '../types';
import { showCustomAlert } from './CustomAlert';

type LagerReportType = 'both' | 'production' | 'transport';

const formatDateSr = (d: Date) => d.toLocaleDateString('sr-RS');

const escapeHtml = (s: any) => {
  const str = String(s ?? '');
  return str
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
};

const buildPrintableHtml = (title: string, htmlBody: string) => `<!doctype html>
<html lang="sr">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(title)}</title>
  <style>
    body { font-family: Arial, Helvetica, sans-serif; padding: 24px; color: #111; }
    h1 { margin: 0 0 8px; font-size: 18px; }
    h2 { margin: 18px 0 8px; font-size: 14px; }
    .meta { font-size: 12px; color: #333; margin-bottom: 12px; }
    .meta div { margin: 2px 0; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; }
    th, td { border: 1px solid #ddd; padding: 6px 8px; vertical-align: top; }
    th { background: #f5f5f5; text-align: left; }
    .right { text-align: right; }
    .muted { color: #555; }
    .toolbar { display:flex; justify-content: flex-end; gap: 8px; margin-bottom: 12px; }
    .btn { padding: 8px 10px; border: 1px solid #ddd; background: #fff; cursor: pointer; font-size: 12px; }
    @media print {
      .toolbar { display:none; }
      body { padding: 0; }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <button class="btn" onclick="window.print()">Sačuvaj kao PDF (Print)</button>
  </div>
  ${htmlBody}
</body>
</html>`;

const openPrintWindow = (title: string, htmlBody: string) => {
  const html = buildPrintableHtml(title, htmlBody);

  try {
    const w = window.open('about:blank', '_blank');
    if (!w) return false;
    w.document.open();
    w.document.write(html);
    w.document.close();
    return true;
  } catch (err) {
    try {
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return true;
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('Failed to open printable report window:', err, e2);
      return false;
    }
  }
};

const computeTransportM2 = (t: LagerTransport, productMap: Map<string, LagerFinishedProduct>): number => {
  const qty = Number(t.quantity || 0);
  const unit = t.unit;

  // Prefer explicit squareMeters if present
  let m2 = Number((t as any).squareMeters ?? 0);
  if (!m2 && unit === 'm2') return qty;

  const productId =
    (t as any).originalProductId || (typeof t.productId === 'string' ? t.productId : t.productId?._id) || '';
  const product = productId ? productMap.get(productId) : undefined;

  if (!product) return m2 || 0;

  const m2PerPiece = Number(product.m2PerPiece || 0);
  const perPallet = Number(product.perPallet || 0);

  if (unit === 'kom' && m2PerPiece) return qty * m2PerPiece;
  if (unit === 'paleta' && m2PerPiece && perPallet) return qty * perPallet * m2PerPiece;

  return m2 || 0;
};

const LagerReports: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [reportType, setReportType] = useState<LagerReportType>('both');

  const [products, setProducts] = useState<LagerFinishedProduct[]>([]);
  const [production, setProduction] = useState<LagerProductionRecord[]>([]);
  const [transports, setTransports] = useState<LagerTransport[]>([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [prods, prod, trans] = await Promise.all([
          lagerFinishedProductsAPI.getAll(),
          lagerProductionAPI.getAll({ showAll: true }),
          lagerTransportAPI.getAll()
        ]);
        setProducts(Array.isArray(prods) ? prods : []);
        setProduction(Array.isArray(prod) ? prod : []);
        setTransports(Array.isArray(trans) ? trans : []);
      } catch (e: any) {
        const msg = e?.response?.data?.message || e?.message || 'Greška pri učitavanju podataka za izveštaj.';
        setError(msg);
        showCustomAlert.error(msg);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const handleGenerate = () => {
    setError('');

    if (!fromDate || !toDate) {
      setError('Početni i završni datum su obavezni.');
      return;
    }
    if (dayjs(toDate).isBefore(dayjs(fromDate), 'day')) {
      setError('Završni datum ne može biti pre početnog.');
      return;
    }

    const from = dayjs(fromDate).startOf('day');
    const to = dayjs(toDate).endOf('day');

    const productMap = new Map<string, LagerFinishedProduct>();
    for (const p of products) productMap.set(p._id, p);

    const filteredProd =
      reportType === 'transport'
        ? []
        : production.filter((r) => {
            const d = dayjs(r.producedDate);
            return d.valueOf() >= from.valueOf() && d.valueOf() <= to.valueOf();
          });

    const filteredTrans =
      reportType === 'production'
        ? []
        : transports.filter((t) => {
            const d = dayjs(t.shippedDate as any);
            return d.valueOf() >= from.valueOf() && d.valueOf() <= to.valueOf();
          });

    if (!filteredProd.length && !filteredTrans.length) {
      setError('Nema zapisa za izabrani period.');
      return;
    }

    const totalProducedM2 = filteredProd.reduce((s, r) => s + Number(r.m2Total || 0), 0);
    const totalTransportM2 = filteredTrans.reduce((s, t) => s + computeTransportM2(t, productMap), 0);
    const netM2 = totalProducedM2 - totalTransportM2;

    const periodStr = `${formatDateSr(fromDate)} - ${formatDateSr(toDate)}`;

    let body = `
      <h1>Izveštaj - Lager gotovih proizvoda</h1>
      <div class="meta">
        <div><strong>Period:</strong> ${escapeHtml(periodStr)}</div>
        <div><strong>Ukupno proizvedeno (m²):</strong> ${escapeHtml(totalProducedM2.toFixed(2))}</div>
        <div><strong>Ukupno transportovano (m²):</strong> ${escapeHtml(totalTransportM2.toFixed(2))}</div>
        <div><strong>Trenutno stanje (m², proizvedeno - transportovano):</strong> ${escapeHtml(netM2.toFixed(2))}</div>
        <div class="muted">Generisano: ${escapeHtml(new Date().toLocaleString('sr-RS'))}</div>
      </div>
    `;

    if (filteredProd.length) {
      body += `<h2>Proizvodnja</h2>`;
      body += `<table>
        <thead>
          <tr>
            <th>Datum</th>
            <th>Proizvod</th>
            <th class="right">Količina</th>
            <th>Jedinica</th>
            <th class="right">m²</th>
            <th>Smenovođa</th>
            <th>Smena</th>
          </tr>
        </thead>
        <tbody>
          ${filteredProd
            .slice()
            .sort((a, b) => (a.producedDate < b.producedDate ? 1 : a.producedDate > b.producedDate ? -1 : 0))
            .map((r) => {
              const d = new Date(r.producedDate);
              const dateStr = isNaN(d.getTime()) ? r.producedDate : formatDateSr(d);
              const shiftLabel = r.shift === 'I' ? 'I smena' : r.shift === 'II' ? 'II smena' : 'III smena';
              return `<tr>
                <td>${escapeHtml(dateStr)}</td>
                <td>${escapeHtml(r.productName)}</td>
                <td class="right">${escapeHtml(r.quantity.toString())}</td>
                <td>${escapeHtml(r.unit)}</td>
                <td class="right">${escapeHtml((Number(r.m2Total || 0)).toFixed(2))}</td>
                <td>${escapeHtml(r.shiftSupervisor || '')}</td>
                <td>${escapeHtml(shiftLabel)}</td>
              </tr>`;
            })
            .join('')}
        </tbody>
      </table>`;
    }

    if (filteredTrans.length) {
      body += `<h2>Transport</h2>`;
      body += `<table>
        <thead>
          <tr>
            <th>Datum</th>
            <th>Proizvod</th>
            <th>Firma</th>
            <th class="right">Količina</th>
            <th>Jedinica</th>
            <th class="right">m²</th>
            <th>Isporučeno</th>
            <th>Napomena</th>
          </tr>
        </thead>
        <tbody>
          ${filteredTrans
            .slice()
            .sort((a, b) => ((a.shippedDate as any) < (b.shippedDate as any) ? 1 : (a.shippedDate as any) > (b.shippedDate as any) ? -1 : 0))
            .map((t) => {
              const d = new Date(t.shippedDate as any);
              const dateStr = isNaN(d.getTime()) ? (t.shippedDate as any) : formatDateSr(d);
              const productName =
                typeof t.productId !== 'string' ? t.productId?.name || t.productName || '' : t.productName || '';
              const companyName =
                typeof t.companyId !== 'string' ? t.companyId?.name || t.companyName || '' : t.companyName || '';
              const m2 = computeTransportM2(t, productMap);
              return `<tr>
                <td>${escapeHtml(dateStr)}</td>
                <td>${escapeHtml(productName)}</td>
                <td>${escapeHtml(companyName)}</td>
                <td class="right">${escapeHtml((t.quantity ?? '').toString())}</td>
                <td>${escapeHtml(t.unit === 'm2' ? 'm²' : t.unit)}</td>
                <td class="right">${escapeHtml(m2.toFixed(2))}</td>
                <td>${escapeHtml((t as any).shippedTo || '')}</td>
                <td>${escapeHtml((t as any).notes || '')}</td>
              </tr>`;
            })
            .join('')}
        </tbody>
      </table>`;
    }

    const ok = openPrintWindow('Izveštaj - Lager gotovih proizvoda', body);
    if (!ok) {
      showCustomAlert.error('Pregled izveštaja nije mogao da se otvori u novom prozoru.');
    }
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <Card title="Izveštaji">
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="p-fluid grid" style={{ rowGap: '1rem', marginBottom: '1.5rem' }}>
          <div className="col-12 md:col-6">
            <label htmlFor="fromDate" className="block mb-2 text-sm font-medium text-gray-700">
              Početni datum
            </label>
            <Calendar
              id="fromDate"
              value={fromDate}
              onChange={(e) => setFromDate(e.value as Date | null)}
              dateFormat="dd.mm.yy"
              placeholder="Izaberite početni datum"
              showIcon
              appendTo="self"
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="toDate" className="block mb-2 text-sm font-medium text-gray-700">
              Završni datum
            </label>
            <Calendar
              id="toDate"
              value={toDate}
              onChange={(e) => setToDate(e.value as Date | null)}
              dateFormat="dd.mm.yy"
              placeholder="Izaberite završni datum"
              showIcon
              appendTo="self"
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <div className="mb-2 text-sm font-medium text-gray-700">Tip izveštaja</div>
          <div className="flex align-items-center gap-4">
            <div className="flex align-items-center gap-2">
              <RadioButton
                inputId="lager-report-both"
                value="both"
                onChange={(e) => setReportType(e.value as LagerReportType)}
                checked={reportType === 'both'}
              />
              <label htmlFor="lager-report-both">Proizvodnja i Transport</label>
            </div>
            <div className="flex align-items-center gap-2">
              <RadioButton
                inputId="lager-report-production"
                value="production"
                onChange={(e) => setReportType(e.value as LagerReportType)}
                checked={reportType === 'production'}
              />
              <label htmlFor="lager-report-production">Samo Proizvodnja</label>
            </div>
            <div className="flex align-items-center gap-2">
              <RadioButton
                inputId="lager-report-transport"
                value="transport"
                onChange={(e) => setReportType(e.value as LagerReportType)}
                checked={reportType === 'transport'}
              />
              <label htmlFor="lager-report-transport">Samo Transport</label>
            </div>
          </div>
        </div>

        <Button
          label="Generiši PDF"
          icon="pi pi-file-pdf"
          loading={loading}
          onClick={handleGenerate}
          style={{ width: '100%' }}
        />
      </Card>
    </div>
  );
};

export default LagerReports;


